/* IMS hero rotator.

   Two modes share the same slide-in animation:

   1. Per-tile (image collage / single image). Each .ims-hero__slider tile
      holds stacked .ims-hero__slide elements and cycles through them on a
      timer, sliding the next one in from the edges listed in data-directions.

   2. Pooled (category cards). The .ims-hero__collage carries data-pool and
      each tile shows one card; the remaining cards wait in .ims-hero__pool.
      On each tick a tile pulls a *random* card that isn't currently on
      screen, resizes it for that tile and slides it in from a random edge,
      then returns the old card to the pool — so every category turns up in
      every block over time rather than each tile cycling a fixed set.

   Timers are staggered per tile via data-delay (plus a little jitter in
   pooled mode) so the collage never swaps everything at once. */

(() => {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const DIRECTION_CLASSES = ['from-left', 'from-right', 'from-top', 'from-bottom'];
  const SIZE_CLASSES = ['ims-hero-card--large', 'ims-hero-card--small'];

  const pick = (list) => list[Math.floor(Math.random() * list.length)];

  /* Slide `next` in over `prev` inside `tile`, then call `finish`. */
  const animateIn = (next, direction, finish) => {
    if (reducedMotion.matches) {
      finish();
      return;
    }

    next.classList.add('from-' + direction, 'is-entering');
    next.getBoundingClientRect(); // flush styles so the slide starts off-screen
    next.classList.add('is-in');

    let done = false;
    const onDone = () => {
      if (done) return;
      done = true;
      next.removeEventListener('transitionend', onDone);
      finish();
    };
    next.addEventListener('transitionend', onDone);
    setTimeout(onDone, 900); // fallback in case transitionend never fires
  };

  const clearAnimationClasses = (slide) => {
    slide.classList.remove('is-entering', 'is-in', ...DIRECTION_CLASSES);
  };

  /* ---- Mode 1: per-tile stacked slides ---- */

  const initSlider = (tile, heroIsVisible) => {
    if (tile.dataset.rotatorInit) return;
    tile.dataset.rotatorInit = 'true';

    const slides = Array.from(tile.querySelectorAll('.ims-hero__slide'));
    const interval = parseInt(tile.dataset.interval, 10);
    if (slides.length < 2 || !interval) return;

    const directions = (tile.dataset.directions || 'left').split(',');
    const delay = parseInt(tile.dataset.delay, 10) || 0;

    let current = Math.max(slides.findIndex((s) => s.classList.contains('is-current')), 0);
    let directionIndex = 0;
    let animating = false;

    const swap = () => {
      if (animating) return;
      animating = true;

      const prev = slides[current];
      current = (current + 1) % slides.length;
      const next = slides[current];

      const direction = directions[directionIndex % directions.length];
      directionIndex += 1;

      animateIn(next, direction, () => {
        prev.classList.remove('is-current');
        next.classList.add('is-current');
        clearAnimationClasses(next);
        animating = false;
      });
    };

    const tick = () => {
      if (!document.hidden && heroIsVisible()) swap();
      setTimeout(tick, interval);
    };
    setTimeout(tick, interval + delay);
  };

  /* ---- Mode 2: shared pool, random tile placement ---- */

  const initPool = (collage, pool, heroIsVisible) => {
    if (collage.dataset.rotatorInit) return;
    collage.dataset.rotatorInit = 'true';

    const interval = parseInt(collage.dataset.interval, 10);
    const tiles = Array.from(collage.querySelectorAll('.ims-hero__slider'));
    const spare = Array.from(pool.querySelectorAll('.ims-hero__slide'));
    if (!interval || tiles.length === 0 || spare.length === 0) return;

    // Pool cards are lazy-loaded and hidden, so the browser won't fetch
    // them on its own. Warm them up now so a card never slides in blank.
    const warm = () => {
      spare.forEach((slide) => {
        slide.querySelectorAll('img[loading="lazy"]').forEach((img) => {
          img.loading = 'eager';
        });
      });
    };
    if ('requestIdleCallback' in window) {
      requestIdleCallback(warm, { timeout: 1500 });
    } else {
      setTimeout(warm, 500);
    }

    const setSize = (slide, size) => {
      const card = slide.querySelector('.ims-hero-card');
      if (!card) return;
      card.classList.remove(...SIZE_CLASSES);
      card.classList.add('ims-hero-card--' + size);
    };

    const swap = (tile) => {
      if (tile.dataset.animating || spare.length === 0) return;
      tile.dataset.animating = 'true';

      const prev = tile.querySelector('.ims-hero__slide.is-current');
      const next = spare.splice(Math.floor(Math.random() * spare.length), 1)[0];
      const directions = (tile.dataset.directions || 'left').split(',');

      setSize(next, tile.dataset.cardSize || 'small');
      tile.appendChild(next);

      animateIn(next, pick(directions), () => {
        if (prev) {
          prev.classList.remove('is-current');
          pool.appendChild(prev);
          spare.push(prev);
        }
        next.classList.add('is-current');
        clearAnimationClasses(next);
        delete tile.dataset.animating;
      });
    };

    tiles.forEach((tile) => {
      const delay = parseInt(tile.dataset.delay, 10) || 0;
      // ±20% jitter so the tiles don't fall into a fixed, predictable rhythm.
      const jitter = () => interval * (0.8 + Math.random() * 0.4);

      const tick = () => {
        if (!document.hidden && heroIsVisible()) swap(tile);
        setTimeout(tick, jitter());
      };
      setTimeout(tick, interval + delay);
    });
  };

  /* ---- Boot ---- */

  const init = () => {
    document.querySelectorAll('.ims-hero').forEach((hero) => {
      let visible = true;
      if ('IntersectionObserver' in window) {
        visible = false;
        new IntersectionObserver(
          (entries) => {
            visible = entries[0].isIntersecting;
          },
          { threshold: 0.15 }
        ).observe(hero);
      }
      const heroIsVisible = () => visible;

      const pooledCollage = hero.querySelector('.ims-hero__collage[data-pool]');
      const pool = hero.querySelector('.ims-hero__pool');
      if (pooledCollage && pool) {
        initPool(pooledCollage, pool, heroIsVisible);
        return;
      }

      hero.querySelectorAll('.ims-hero__slider').forEach((tile) => initSlider(tile, heroIsVisible));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
